import {
  Button,
  Chip,
  Grid2,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import { CopyButton } from '@components/CopyButton';

import {
  useCreateDhaShareLink,
  useCreateShareLink,
} from '@hooks/api/shared_resource';

import { ShareType } from 'generated/types';

const messages = defineMessages({
  helperText: {
    id: 'shareButton.shareDialog.helperText',
    defaultMessage:
      'Copy the link and share it however you prefer. The link can be used to access this record until it expires.',
  },
  close: {
    id: 'shareButton.shareDialog.close',
    defaultMessage: 'Done',
  },
  availableUntil: {
    id: 'shareButton.shareDialog.availableUntil',
    defaultMessage:
      'This link is available to these people for the next {until}:',
  },
  error: {
    id: 'shareButton.shareDialog.error',
    defaultMessage: "Something went wrong and the link couldn't be created.",
  },
  retry: {
    id: 'shareButton.shareDialog.retry',
    defaultMessage: 'Try again',
  },
});

interface ShareLinkDialogContentProps {
  /**
   * The ID of the VITALL patient.
   */
  vitall_patient_id: string;
  /**
   * The ID of the resource model.
   */
  resource_model_id?: string;
  /**
   * The list of recipients for the share link.
   */
  recipients: string[];
  /**
   * The expiration time of the share link in hours.
   */
  expires: number;
  /**
   * Callback function to be called when the close button is clicked.
   *
   * @return {void}
   */
  onClose: () => void;
  /**
   * The type of resources being shared
   */
  share_type: ShareType;
  /**
   * The DHA being shared. Required for ShareType.DigitalHealthAssessment.
   */
  assessment_id?: string;
}

const formatExpiry = (expires: number) => {
  const duration = expires;
  if (duration < 24) {
    return `${duration} hour${duration > 1 ? 's' : ''}`;
  } else if (duration < 168) {
    return `${Math.ceil(duration / 24)} day${duration > 47 ? 's' : ''}`;
  } else {
    return `${Math.ceil(duration / 168)} week${duration > 111 ? 's' : ''}`;
  }
};

export const ShareLinkDialogContent = ({
  vitall_patient_id,
  expires,
  recipients,
  resource_model_id,
  onClose,
  share_type,
  assessment_id,
}: ShareLinkDialogContentProps) => {
  const [shareLink, setShareLink] = useState<string>('');
  // Tracks a failed share-link creation so we can show a retry affordance
  // instead of silently falling through to the "success" view with an empty
  // link -- createShareLink/createDhaShareLink can genuinely fail server-side
  // (e.g. the DHA is no longer complete), and swallowing that produced a
  // dialog that looked successful but wasn't.
  const [error, setError] = useState(false);
  const [createShareLink, creatingShareLink] = useCreateShareLink();
  const [createDhaShareLink, creatingDhaShareLink] = useCreateDhaShareLink();
  const { formatMessage } = useIntl();
  // DHA goes through its own mutation so practitioners and org admins can be
  // granted DHA sharing without unlocking every other share type -- permissions
  // are keyed by operation name.
  const isDha = share_type === ShareType.DigitalHealthAssessment;
  const loading = isDha ? creatingDhaShareLink : creatingShareLink;

  const createLink = useCallback(async () => {
    setError(false);
    const input = {
      vitall_patient_id,
      expires: dayjs().add(expires, 'hour').toDate(),
      recipients,
      share_type,
    };
    try {
      if (isDha) {
        const { data, errors } = await createDhaShareLink({
          input: { ...input, assessment_id },
        });
        const link = data?.createDhaShareLink.share_link;
        if (errors || !link) {
          setError(true);
          return;
        }
        setShareLink(link);
        return;
      }
      const { data, errors } = await createShareLink({
        input: { ...input, resource_model_id },
      });
      const link = data?.createShareLink.share_link;
      if (errors || !link) {
        setError(true);
        return;
      }
      setShareLink(link);
    } catch {
      setError(true);
    }
  }, [
    assessment_id,
    createDhaShareLink,
    createShareLink,
    expires,
    isDha,
    recipients,
    resource_model_id,
    share_type,
    vitall_patient_id,
  ]);

  useEffect(() => {
    createLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    expires,
    recipients,
    resource_model_id,
    assessment_id,
    vitall_patient_id,
  ]);

  const handleClose = () => {
    onClose();
  };

  if (error) {
    return (
      <ContentLoader loading={loading}>
        <Stack spacing={2}>
          <Typography variant="body2" color="error">
            {formatMessage(messages.error)}
          </Typography>
          <Button variant="contained" onClick={createLink}>
            {formatMessage(messages.retry)}
          </Button>
          <Button variant="text" onClick={handleClose}>
            {formatMessage(messages.close)}
          </Button>
        </Stack>
      </ContentLoader>
    );
  }

  return (
    <ContentLoader loading={loading}>
      <Stack spacing={2}>
        <TextField
          variant="outlined"
          value={shareLink}
          helperText={formatMessage(messages.helperText)}
          InputProps={{
            readOnly: true,
            endAdornment: <CopyButton content={shareLink} />,
          }}
          fullWidth
        />
        <Stack spacing={0}>
          <Typography variant="body2">
            {formatMessage(messages.availableUntil, {
              until: formatExpiry(expires),
            })}
          </Typography>
          <Grid2 container spacing={1}>
            {recipients.map((recipient) => (
              <Grid2 key={recipient}>
                <Chip
                  label={recipient}
                  variant="outlined"
                  sx={{ textTransform: 'none' }}
                />
              </Grid2>
            ))}
          </Grid2>
        </Stack>
        <Button variant="contained" onClick={handleClose}>
          {formatMessage(messages.close)}
        </Button>
      </Stack>
    </ContentLoader>
  );
};
